import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { HubConnectionBuilder, LogLevel, type HubConnection } from '@microsoft/signalr';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { errorHandlerWrapper } from 'app/main/components/Utils';
import { addNotification, setNotifications } from './notificationsSlice';

export const initConnection = createAsyncThunk<HubConnection | null>(
	'notificationsConnectionManager/state/initConnection',
	async (_params, { dispatch }) => {
		const connection = new HubConnectionBuilder()
			.withUrl(`${axios.defaults.baseURL}/hubs/notifications`, {
				accessTokenFactory: () => window.localStorage.getItem('accessToken') || ''
			})
			.configureLogging(LogLevel.Error)
			.build();

		connection.on('newConnection', connInfo => {
			if (import.meta.env.DEV) {
				console.log('[Notification Hub] New connection', connInfo);
			}
		});

		connection.on('fetchNotifications', notifications => dispatch(setNotifications(notifications || [])));

		connection.on('newNotification', notification => dispatch(addNotification(notification)));

		await connection.start();

		return connection
			.invoke('addNewConnection')
			.then(() => connection)
			.catch(error => {
				errorHandlerWrapper({ dispatch, error });
				return null;
			});
	}
);

export const sendNotification = createAsyncThunk<void, { message: any }, { state: any }>(
	'notificationsConnectionManager/state/sendNotification',
	async ({ message }, { dispatch, getState }) => {
		const { connection } = getState().notificationsManager;

		connection
			.invoke('send', { id: uuidv4(), ...message })
			// .then(() => connection)
			.catch(error => errorHandlerWrapper({ dispatch, error }));
	}
);

export const readNotification = createAsyncThunk<void, { notificationUid: string }, { state: any }>(
	'notificationsConnectionManager/state/readNotification',
	async ({ notificationUid }, { dispatch, getState }) => {
		const { connection } = getState().notificationsManager;

		connection
			.invoke('read', notificationUid)
			// .then(() => connection)
			.catch(error => errorHandlerWrapper({ dispatch, error }));
	}
);

export const dismissNotification = createAsyncThunk<void, { notificationUid: string }, { state: any }>(
	'notificationsConnectionManager/state/dismissNotification',
	async ({ notificationUid }, { dispatch, getState }) => {
		const { connection } = getState().notificationsManager;

		connection
			.invoke('dismiss', [notificationUid])
			// .then(() => connection)
			.catch(error => errorHandlerWrapper({ dispatch, error }));
	}
);

export const dismissAllNotifications = createAsyncThunk<void, void, { state: any }>(
	'notificationsConnectionManager/state/dismissAllNotifications',
	async (_params, { dispatch, getState }) => {
		const { connection, notifications } = getState().notificationsManager;

		connection
			.invoke('dismiss', notifications.ids || [])
			// .then(() => connection)
			.catch(error => errorHandlerWrapper({ dispatch, error }));
	}
);

const initialState: HubConnection | null = null;

const notificationsConnectionSlice = createSlice({
	name: 'notificationsConnectionManager/state',
	initialState,
	reducers: {},
	extraReducers: builder => {
		builder.addCase(initConnection.fulfilled, (state: HubConnection | null, action) => {
			state = action.payload;
		});
	}
});

export default notificationsConnectionSlice.reducer;
